interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  breadcrumbs?: BreadcrumbItem[];
  articleData?: {
    publishedTime: string;
    modifiedTime: string;
    author: string;
    tags: string[];
  };
  faqData?: Array<{ question: string; answer: string }>;
  type?: 'website' | 'article' | 'webpage';
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  breadcrumbs,
  articleData,
  faqData,
  type = 'webpage',
}: SEOHeadProps) {
  const siteName = '风车动漫';
  const fullTitle = `${title} - ${siteName}`;

  const schemas: object[] = [];

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: canonicalUrl.split('/').slice(0, 3).join('/'),
    description: '风车动漫网是国内领先的手机动漫门户网站，专注为移动端用户提供优质的动漫内容服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@fengchedongman.com',
      contactType: 'customer service',
    },
  });

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: canonicalUrl.split('/').slice(0, 3).join('/'),
    potentialAction: {
      '@type': 'SearchAction',
      target: `${canonicalUrl.split('/').slice(0, 3).join('/')}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  });

  const webpageSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebPage',
    name: fullTitle,
    description,
    url: canonicalUrl,
  };

  if (articleData) {
    webpageSchema.datePublished = articleData.publishedTime;
    webpageSchema.dateModified = articleData.modifiedTime;
    webpageSchema.author = {
      '@type': 'Person',
      name: articleData.author,
    };
    webpageSchema.keywords = articleData.tags.join(',');
  }

  schemas.push(webpageSchema);

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    });
  }

  if (faqData && faqData.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  const jsonLd = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas, null, 0);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      <script
        dangerouslySetInnerHTML={{
          __html: `document.title = ${JSON.stringify(fullTitle)};
var metaDesc = document.querySelector('meta[name="description"]');
if (metaDesc) metaDesc.setAttribute('content', ${JSON.stringify(description)});
var metaKw = document.querySelector('meta[name="keywords"]');
if (metaKw) metaKw.setAttribute('content', ${JSON.stringify(keywords)});
var canonical = document.querySelector('link[rel="canonical"]');
if (canonical) canonical.setAttribute('href', ${JSON.stringify(canonicalUrl)});
var ogTitle = document.querySelector('meta[property="og:title"]');
if (ogTitle) ogTitle.setAttribute('content', ${JSON.stringify(ogTitle || fullTitle)});
var ogDesc = document.querySelector('meta[property="og:description"]');
if (ogDesc) ogDesc.setAttribute('content', ${JSON.stringify(ogDescription || description)});
var ogType = document.querySelector('meta[property="og:type"]');
if (ogType) ogType.setAttribute('content', ${JSON.stringify(ogType)});
var ogUrl = document.querySelector('meta[property="og:url"]');
if (ogUrl) ogUrl.setAttribute('content', ${JSON.stringify(canonicalUrl)});
var twTitle = document.querySelector('meta[name="twitter:title"]');
if (twTitle) twTitle.setAttribute('content', ${JSON.stringify(ogTitle || fullTitle)});
var twDesc = document.querySelector('meta[name="twitter:description"]');
if (twDesc) twDesc.setAttribute('content', ${JSON.stringify(ogDescription || description)});`,
        }}
      />
    </>
  );
}